import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function AboutSection() {
  const entryTransition = { duration: 0.6, ease: 'easeOut' };
  const springTransition = { type: 'spring', stiffness: 120, damping: 20 };

  const getItemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: (delay = 0) => ({
      opacity: 1,
      y: 0,
      transition: { ...entryTransition, delay },
    }),
  };

  // Sombra y transición unificadas
  const initialShadow = '1px 4px 7px 2px rgba(0,0,0,0.10)';
  const hoverShadow = '1px 4px 10px 3px rgba(94,94,94,0.25)';
  const shadowTransition = { duration: 0.3, ease: 'easeInOut' };

  const VideoEmbed = ({ videoId, title, delay = 0, className = '' }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [playWithSound, setPlayWithSound] = useState(false);
    const iframeRef = useRef(null);
    const embedUrl = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=1&playsinline=1&rel=0&modestbranding=1&origin=${encodeURIComponent(window.location.origin)}`;

    const thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    const fallbackThumbnail = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    const [thumb, setThumb] = useState(thumbnail);
    const handleImageError = () => setThumb(fallbackThumbnail);

    const sendYouTubeCommand = (func, args = []) => {
      if (!iframeRef.current?.contentWindow) return;

      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({
          event: 'command',
          func,
          args,
        }),
        '*'
      );
    };

    const handlePlayRequest = () => {
      setPlayWithSound(true);
      setIsPlaying(true);
    };

    const handleIframeLoad = () => {
      if (!playWithSound) return;

      sendYouTubeCommand('playVideo');
      sendYouTubeCommand('unMute');
      setTimeout(() => {
        sendYouTubeCommand('playVideo');
        sendYouTubeCommand('unMute');
      }, 250);
    };

    // Solo permite hover si no está el iframe
    const enableHover = !isPlaying;

    return (
      <motion.div
        className={`relative w-full aspect-video rounded-2xl overflow-hidden cursor-pointer shadow-sm flex-1 h-full ${className}`}
        onClick={handlePlayRequest}
        onMouseEnter={() => enableHover && setIsHovered(true)}
        onMouseLeave={() => enableHover && setIsHovered(false)}
        onTouchStart={() => enableHover && setIsHovered(true)}
        onTouchEnd={() => enableHover && setIsHovered(false)}
        onTouchCancel={() => enableHover && setIsHovered(false)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        custom={delay}
        variants={getItemVariants}
        animate={{
          boxShadow: enableHover && isHovered ? hoverShadow : initialShadow,
          scale: enableHover && isHovered ? 1.03 : 1,
        }}
        transition={{
          boxShadow: shadowTransition,
          scale: springTransition,
        }}
      >
        {!loaded && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse" />
        )}

        <motion.img
          src={thumb}
          alt={title}
          onError={handleImageError}
          onLoad={() => setLoaded(true)}
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ opacity: 0 }}
          animate={{ opacity: loaded ? 1 : 0 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        />

        <AnimatePresence>
          {!isPlaying && loaded && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <motion.div
                className="absolute inset-0 bg-black/40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              />
              <motion.div
                className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm relative z-10"
                initial={{ opacity: 0 }}
                animate={{
                  scale: [1, 1.05, 1],
                  opacity: [0.9, 1, 0.9],
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  repeatType: 'loop',
                  ease: 'easeInOut',
                }}
              >
                <motion.svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  className="w-8 h-8 text-customBlue"
                >
                  <path d="M8 5v14l11-7z" />
                </motion.svg>
              </motion.div>
            </motion.div>
          )}
          {isPlaying && (
            <motion.iframe
              key="iframe"
              ref={iframeRef}
              className="absolute inset-0 w-full h-full rounded-2xl shadow-sm"
              src={embedUrl}
              title={title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              onLoad={handleIframeLoad}
              allowFullScreen
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeInOut', delay: 0.05 }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  const infoDivClass = `
    relative bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 md:p-10 shadow-sm overflow-hidden flex flex-col justify-center items-center flex-1 h-full
  `;

  const Border = ({ color, position, isHovered }) => {
    const baseSize = 28;
    const hoverIncrease = 20;
    const size = isHovered ? baseSize + hoverIncrease : baseSize;

    return (
      <motion.div
        className={`absolute ${color} ${
          position === 'top-left'
            ? 'top-0 left-0 rounded-br-2xl'
            : 'bottom-0 right-0 rounded-tl-2xl'
        }`}
        initial={{ width: baseSize, height: baseSize }}
        animate={{
          width: size,
          height: size,
        }}
        transition={springTransition}
      />
    );
  };

  const InfoBlock = ({ title, children, className = '', delay = 0 }) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <motion.div
        className={`${infoDivClass} ${className}`}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        custom={delay}
        variants={getItemVariants}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={() => setIsHovered(true)}
        onTouchEnd={() => setIsHovered(false)}
        onTouchCancel={() => setIsHovered(false)}
        whileHover={{
          scale: 1.03,
        }}
        animate={{
          boxShadow: isHovered ? hoverShadow : initialShadow,
          scale: isHovered ? 1.03 : 1,
        }}
        transition={{
          boxShadow: shadowTransition,
          scale: springTransition,
        }}
      >
        <Border
          color="bg-yellow-200/40"
          position="top-left"
          isHovered={isHovered}
        />
        <Border
          color="bg-indigo-200/40"
          position="bottom-right"
          isHovered={isHovered}
        />
        <div className="flex flex-col justify-center items-center text-center">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold font-heading mb-3 text-gray-700">
            {title}
          </h2>
          <p className="text-gray-400 text-sm sm:text-md md:text-lg leading-relaxed">
            {children}
          </p>
        </div>
      </motion.div>
    );
  };

  const blocks = [
    {
      type: 'info',
      title: 'Propósito superior',
      content:
        'La inmobiliaria Sol Naciente busca captar y comercializar propiedad raíz en el Oriente antioqueño con precios asequibles en el mercado, satisfacer las necesidades de nuestros clientes, brindando la mejor asesoría y acompañamiento durante y después del proceso de venta y compra de inmuebles. Asimismo, comprometernos para generar un mayor bienestar a las personas brindando productos con altos estándares de calidad y encontrar la propiedad de sus sueños.',
    },
    {
      type: 'video',
      videoId: 'NHMV_vZpCOo',
      title: 'Video 1',
    },
    {
      type: 'info',
      title: 'Objetivo empresarial',
      content:
        'Para el año 2030 la inmobiliaria Sol Naciente, habrá alcanzado un posicionamiento significativo en el mercado de venta y renta a nivel regional, siendo una empresa competitiva, reconocida y generadora de empleo para su comunidad, mediante la captación y colocación de propiedad raíz, cumpliendo con los requerimientos de la demanda, generando nuevas estrategias que permitan marcar la diferencia frente a la competencia y lograr la satisfacción de los clientes a través de la confianza y el compromiso que se les brinda desde la inmobiliaria.',
    },
    {
      type: 'video',
      videoId: 'NHMV_vZpCOo',
      title: 'Servicios Sol Naciente',
    },
  ];

  return (
    <div className="bg-white pt-8 pb-20 px-4 sm:px-6 md:px-12 lg:px-24 xl:px-32">
      <motion.div
        className="flex flex-col items-center justify-center py-10"
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true }}
        transition={entryTransition}
      >
        <motion.h2
          className="text-2xl sm:text-3xl md:text-4xl tracking-wide font-bold font-heading leading-none text-gray-700 text-center"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={entryTransition}
        >
          ¿Quiénes Somos?
        </motion.h2>

        <motion.div
          className="h-1 w-36 sm:w-40 mt-4 rounded-full bg-gradient-to-r from-customBlue via-customYellow/50 to-customBlue"
          style={{ backgroundSize: '200% 100%' }}
          animate={{ backgroundPosition: ['0% 0', '100% 0', '0% 0'] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 lg:gap-20 mt-4 sm:mt-10 items-stretch">
        {blocks.map((block, index) => {
          const delay = index * 0.1;
          const orderClasses = [
            'order-1 lg:order-1',
            'order-2 lg:order-2',
            'order-3 lg:order-4',
            'order-4 lg:order-3',
          ];

          if (block.type === 'info') {
            return (
              <InfoBlock
                key={index}
                title={block.title}
                delay={delay}
                className={orderClasses[index]}
              >
                {block.content}
              </InfoBlock>
            );
          } else if (block.type === 'video') {
            return (
              <VideoEmbed
                key={index}
                videoId={block.videoId}
                title={block.title}
                delay={delay}
                className={orderClasses[index]}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}
